import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Menu,
    MenuItem,
    Pagination,
    Stack,
    Typography
} from '@mui/material';
import { Close, ExpandMore } from '@mui/icons-material';
import Masonry from '@mui/lab/Masonry';
import axios from 'axios';
import ChartInteractionIndicator from '../../common/ChartInteractionIndicator';
import PostCard from '../../post/PostCard';
import { closeDialog } from '../../../features/dialog';
import { numberFormat } from 'highcharts';

const DIALOG_ID = 'chartPreview';
const PAGE_SIZE = 100;

function ChartPreviewDialog() {
    const dispatch = useDispatch();
    const dialogState = useSelector(state => state.dialog[DIALOG_ID]);
    const open = !!dialogState;
    const baseFilters = dialogState?.baseFilters || {};
    const clickedFilters = dialogState?.filters || {};
    const projectId = dialogState?.projectId;
    const tabId = dialogState?.tabId;

    const [interactiveFilters, setInteractiveFilters] = useState({ ...baseFilters, ...clickedFilters });
    const [orderBy, setOrderBy] = useState(clickedFilters?.orderBy || baseFilters?.orderBy || 'post_time');
    const [orderDir, setOrderDir] = useState(clickedFilters?.orderDir || baseFilters?.orderDir || 'desc');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [posts, setPosts] = useState([]);
    const [total, setTotal] = useState(0);
    const [sortAnchor, setSortAnchor] = useState(null);

    useEffect(() => {
        if (open) {
            setInteractiveFilters({ ...baseFilters, ...clickedFilters });
            setOrderBy(clickedFilters?.orderBy || baseFilters?.orderBy || 'post_time');
            setOrderDir(clickedFilters?.orderDir || baseFilters?.orderDir || 'desc');
            setPage(1);
            setPosts([]);
            setTotal(0);
            setError(null);
        }
    }, [open, clickedFilters, baseFilters]);

    const combinedFilters = useMemo(() => ({
        ...(interactiveFilters || {}),
        orderBy,
        orderDir,
    }), [interactiveFilters, orderBy, orderDir]);

    const offset = (page - 1) * PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));

    const fetchPosts = async () => {
        if (!open || !projectId || !tabId) return;
        setLoading(true);
        setError(null);
        try {
            const payload = {
                ...combinedFilters,
                project_id: projectId,
                tab_id: tabId,
                offset,
                limit: PAGE_SIZE,
            };
            const { data } = await axios.post('/post/search', payload);
            if (data?.error) {
                setError(data.error);
                setPosts([]);
                setTotal(0);
            } else {
                setPosts(data?.posts || []);
                setTotal(data?.total || 0);
            }
        } catch (err) {
            setError(err.message || 'Failed to load posts');
            setPosts([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, page, orderBy, orderDir, interactiveFilters, baseFilters]);

    const handleClose = () => {
        setPosts([]);
        setTotal(0);
        setError(null);
        setInteractiveFilters({ ...baseFilters, ...clickedFilters });
        setOrderBy(clickedFilters?.orderBy || baseFilters?.orderBy || 'post_time');
        setOrderDir(clickedFilters?.orderDir || baseFilters?.orderDir || 'desc');
        setPage(1);
        dispatch(closeDialog(DIALOG_ID));
    };

    const handleRemoveFilter = (key) => {
        setInteractiveFilters(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setPage(1);
    };

    const handleResetFilters = () => {
        setInteractiveFilters({ ...baseFilters });
        setPage(1);
    };

    const isDefaultFilters = useMemo(() => {
        return JSON.stringify(interactiveFilters || {}) === JSON.stringify(baseFilters || {});
    }, [interactiveFilters, baseFilters]);

    const handleSortClick = (event) => {
        setSortAnchor(event.currentTarget);
    };

    const handleSortChange = (newOrder) => {
        setOrderBy(newOrder);
        setOrderDir('desc');
        setSortAnchor(null);
        setPage(1);
    };

    const currentStart = total ? offset + 1 : 0;
    const currentEnd = total ? Math.min(offset + PAGE_SIZE, total) : 0;
    const totalFormatted = numberFormat(total || 0, 0, '.', ',');
    const orderLabel = orderBy === 'engagement' ? 'engagement (desc)' : 'date (newest first)';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xl"
            PaperProps={{ sx: { height: '90vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Chart Filtered Content</Typography>
                <Button onClick={handleClose} startIcon={<Close />} size="small">
                    Close Preview
                </Button>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <ChartInteractionIndicator
                        filters={combinedFilters}
                        onRemoveFilter={handleRemoveFilter}
                    />
                    <Button size="small" variant="outlined" onClick={handleResetFilters} disabled={isDefaultFilters}>
                        Reset to tab filters
                    </Button>
                </Box>

                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2">
                        {total > 0 ? (
                            <>Displaying posts from {currentStart} to {currentEnd} of <b>{totalFormatted}</b>, sorted by <Button size="small" endIcon={<ExpandMore />} onClick={handleSortClick}>{orderLabel}</Button></>
                        ) : (
                            'No posts to display yet'
                        )}
                    </Typography>
                    {totalPages > 1 && (
                        <Stack spacing={1} direction="row" alignItems="center">
                            <Pagination color="secondary" page={page} count={totalPages} onChange={(e, value) => setPage(value)} />
                        </Stack>
                    )}
                </Box>

                <Menu
                    id="chart-preview-sort-menu"
                    anchorEl={sortAnchor}
                    open={Boolean(sortAnchor)}
                    onClose={() => setSortAnchor(null)}
                >
                    <MenuItem onClick={() => handleSortChange('post_time')}>Date (newest first)</MenuItem>
                    <MenuItem onClick={() => handleSortChange('engagement')}>Engagement (desc)</MenuItem>
                </Menu>

                {loading && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <LinearProgress />
                        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                            Loading posts...
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && posts.length === 0 && (
                    <Alert severity="info">No posts found with these filters.</Alert>
                )}

                {!loading && posts.length > 0 && (
                    <Masonry columns={5} spacing={2} sx={{ mt: 1 }}>
                        {posts.map((post, index) => (
                            <Box key={post.uuid || index} minHeight={220}>
                                <PostCard post={post} intent="preview" />
                            </Box>
                        ))}
                    </Masonry>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} startIcon={<Close />} size="small">
                    Close Preview
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ChartPreviewDialog;
